import * as React from 'react'

import { Box, Button, CollapsableCard, Text } from '@factorialco/deprecated-design-system'
import { F0AvatarList, F0Link } from '@factorialco/f0-react'
import { OneAlert } from '@factorialco/f0-react/dist/experimental'
import { SalaryAdvanceEmployeeProfileFragment } from 'generated/resources/graphql'

import Sidepanel from 'modules/core/components/Sidepanel'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { buildUrl } from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

interface EmployeeWithMissingInfo {
  id: number
  firstName: string
  lastName: string
  avatarUrl?: string
}

const MAX_AVATARS_TO_SHOW = 5

interface ResultSectionProps {
  title: string
  employees: EmployeeWithMissingInfo[]
  linkText: string
  linkHref: string
}

const ResultSection: React.FC<ResultSectionProps> = ({ title, employees, linkText, linkHref }) => {
  const [expanded, setExpanded] = React.useState(false)

  if (employees.length === 0) return null

  const sortedEmployees = [...employees].sort((a, b) =>
    `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`)
  )

  const avatars = sortedEmployees.slice(0, MAX_AVATARS_TO_SHOW).map((employee) => ({
    firstName: employee.firstName,
    lastName: employee.lastName,
    src: employee.avatarUrl ? buildUrl(employee.avatarUrl) : undefined,
    type: 'person' as const,
  }))

  let remainingCount: number | undefined = Math.max(0, employees.length - MAX_AVATARS_TO_SHOW)
  remainingCount = remainingCount === 0 ? undefined : remainingCount

  return (
    <Box flexDirection="column" gap="s8">
      <CollapsableCard
        Card={
          <Box
            flexDirection="row"
            justifyContent="spaceBetween"
            alignItems="center"
            onClick={() => setExpanded(!expanded)}
            cursor="pointer"
          >
            <Text size="300" weight="semibold" color="grey1000">
              {`${title}: ${employees.length}`}
            </Text>
            <Button iconName={expanded ? 'chevron-up' : 'chevron-down'} type="secondary" />
          </Box>
        }
        Content={
          <Box
            flexDirection="row"
            alignItems="center"
            justifyContent={remainingCount ? 'spaceBetween' : undefined}
            gap="s8"
          >
            <Box overflowX="hidden" style={{ flex: remainingCount ? undefined : '1' }}>
              <F0AvatarList
                avatars={avatars}
                type="person"
                max={5}
                remainingCount={remainingCount}
              />
            </Box>
            <Box flexNoShrink>
              <F0Link href={linkHref} type="solid" size="sm">
                {linkText}
              </F0Link>
            </Box>
          </Box>
        }
        open={expanded}
      />
    </Box>
  )
}

const mapToEmployeeWithMissingInfo = (
  employee: SalaryAdvanceEmployeeProfileFragment
): EmployeeWithMissingInfo | null => {
  if (!employee.employeeName) return null

  return {
    id: employee.employeeName.id,
    firstName: employee.employeeName.firstName,
    lastName: employee.employeeName.lastName,
    avatarUrl: employee.employeeName.avatar?.url,
  }
}

interface Props {
  open: boolean
  onClose: () => void
  selectedEmployees: SalaryAdvanceEmployeeProfileFragment[]
}

export const MissingInfoSidePanel: React.FC<Props> = ({ open, onClose, selectedEmployees }) => {
  const { navigation } = useNavigation()

  const { employeesWithMissingSalary, employeesWithMissingBank } = useUnsafeMemo(() => {
    const missingSalary: EmployeeWithMissingInfo[] = []
    const missingBank: EmployeeWithMissingInfo[] = []

    selectedEmployees.forEach((employee) => {
      const mappedEmployee = mapToEmployeeWithMissingInfo(employee)
      if (!mappedEmployee) return

      if (employee.missingSalaryInfo) {
        missingSalary.push(mappedEmployee)
      }

      if (employee.missingBankInfo) {
        missingBank.push(mappedEmployee)
      }
    })

    return {
      employeesWithMissingSalary: missingSalary,
      employeesWithMissingBank: missingBank,
    }
  }, [selectedEmployees])

  return (
    <Sidepanel
      open={open}
      onClose={onClose}
      title={i18n.t('salary_advance.employee.missing_info_side_panel.title')}
      closeLabel={i18n.t('common.close')}
      width="wide"
    >
      <Box gap="s32">
        <Box flexDirection="column" gap="s24">
          <OneAlert
            title={i18n.t('salary_advance.employee.missing_info_side_panel.description.title')}
            description={i18n.t('salary_advance.employee.missing_info_side_panel.description.text')}
            variant="warning"
          />

          <Box flexDirection="column" gap="s16">
            <ResultSection
              title={i18n.t('salary_advance.employee.missing_info_side_panel.missing_salary.title')}
              employees={employeesWithMissingSalary}
              linkText={i18n.t(
                'salary_advance.employee.missing_info_side_panel.missing_salary.link_text'
              )}
              linkHref={navigation.employees.list.fullPath()}
            />

            <ResultSection
              title={i18n.t('salary_advance.employee.missing_info_side_panel.missing_iban.title')}
              employees={employeesWithMissingBank}
              linkText={i18n.t(
                'salary_advance.employee.missing_info_side_panel.missing_iban.link_text'
              )}
              linkHref={navigation.employees.list.fullPath()}
            />
          </Box>
        </Box>
      </Box>
    </Sidepanel>
  )
}
